import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { marcas as api, type MarcaNaLista } from '../api/marcas'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './marcas.css'

type Edicao =
  | { modo: 'fechado' }
  | { modo: 'nova' }
  | { modo: 'editando'; marca: MarcaNaLista }

/**
 * Cadastro de marcas.
 *
 * 🔴 **A demanda 018 fechou com "criar e alterar marca" no escopo e esta tela
 * nunca existiu** — o back tinha `GET`, `POST` e `PUT`, o front tinha até o
 * cliente de API, e ninguém o chamava. Enquanto isso `digiorgio` estava escrito
 * à mão em cinco botões da página inicial, e a Letras e Versos era inalcançável.
 */
export function Marcas() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<MarcaNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [edicao, setEdicao] = useState<Edicao>({ modo: 'fechado' })
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  // A criação é de nível CTGF; alterar é gerência comum.
  const cria = pode('ParametroCtgf')

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.listar())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function salvar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (edicao.modo === 'fechado') return

    const dados = new FormData(evento.currentTarget)
    const pedido = {
      nome: String(dados.get('nome') ?? '').trim(),
      telefone: String(dados.get('telefone') ?? '').trim() || null,
      ativa: dados.get('ativa') === 'on',
    }

    setErro(null)
    setSalvando(true)

    try {
      if (edicao.modo === 'nova') await api.criar(pedido)
      else await api.alterar(edicao.marca.chave, pedido)

      setEdicao({ modo: 'fechado' })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : t('marcas.erroAoSalvar'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <>
      <header className="marcas__topo">
        <div>
          <h1 className="marcas__titulo">{t('marcas.titulo')}</h1>
          <p className="marcas__descricao">{t('marcas.descricao')}</p>
        </div>

        {cria && edicao.modo === 'fechado' && (
          <Botao variante="primario" onClick={() => setEdicao({ modo: 'nova' })}>
            {t('marcas.nova')}
          </Botao>
        )}
      </header>

      {edicao.modo !== 'fechado' && (
        <Cartao titulo={edicao.modo === 'nova' ? t('marcas.nova') : t('marcas.editar')}>
          <form onSubmit={salvar} className="marcas__forma">
            <Campo
              name="nome"
              rotulo={t('marcas.nome')}
              obrigatorio
              required
              maxLength={200}
              defaultValue={edicao.modo === 'editando' ? edicao.marca.nome : ''}
              ajuda={
                edicao.modo === 'editando' ? t('marcas.chaveNaoMuda') : t('marcas.chaveDerivada')
              }
            />

            <Campo
              name="telefone"
              rotulo={t('marcas.telefone')}
              maxLength={20}
              defaultValue={edicao.modo === 'editando' ? (edicao.marca.telefone ?? '') : ''}
              ajuda={t('marcas.telefoneAjuda')}
            />

            <label className="marcas__ativa">
              <input
                type="checkbox"
                name="ativa"
                defaultChecked={edicao.modo === 'editando' ? edicao.marca.ativa : true}
              />
              {t('marcas.ativa')}
            </label>

            {erro && <Aviso tom="perigo">{erro}</Aviso>}

            <div className="marcas__acoes">
              <Botao type="submit" variante="primario" carregando={salvando}>
                {t('marcas.salvar')}
              </Botao>

              <Botao type="button" variante="discreto" onClick={() => setEdicao({ modo: 'fechado' })}>
                {t('marcas.cancelar')}
              </Botao>
            </div>
          </form>
        </Cartao>
      )}

      {falhou ? (
        <Estado
          tipo="erro"
          titulo={t('marcas.erroAoCarregar')}
          acao={<Botao onClick={() => void carregar()}>{t('marcas.tentarDeNovo')}</Botao>}
        />
      ) : lista === null ? (
        <Estado tipo="carregando" titulo={t('marcas.carregando')} />
      ) : lista.length === 0 ? (
        <Estado tipo="vazio" titulo={t('marcas.vazio')} descricao={t('marcas.vazioAjuda')} />
      ) : (
        <div className="marcas__tabela-rolagem">
        <table className="marcas__tabela">
          <caption className="marcas__legenda">{t('marcas.legenda')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('marcas.nome')}</th>
              <th scope="col">{t('marcas.chave')}</th>
              <th scope="col">{t('marcas.telefone')}</th>
              <th scope="col">{t('marcas.canais')}</th>
              <th scope="col">{t('marcas.situacao')}</th>
              <th scope="col">{t('marcas.acoes')}</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((marca) => (
              <tr key={marca.chave}>
                <td>
                  <a href={`/marcas/${marca.chave}/assistentes`}>{marca.nome}</a>
                </td>
                <td>
                  <code>{marca.chave}</code>
                </td>
                <td>{marca.telefone ?? '—'}</td>
                <td>{marca.caixas}</td>
                <td>{marca.ativa ? t('marcas.ativaSim') : t('marcas.ativaNao')}</td>
                <td>
                  {cria && (
                    <Botao
                      variante="discreto"
                      onClick={() => setEdicao({ modo: 'editando', marca })}
                    >
                      {t('marcas.editar')}
                    </Botao>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </>
  )
}
